export const EnrollmentStatus = Object.freeze({
  None: 0,

  // Application Stage
  Draft: 1,
  Submitted: 2,
  Returned: 3,

  // Review Stage
  DocumentsPending: 4,
  PaymentPending: 5,
  PaymentReceived: 6,

  // Approval Stage
  Approved: 7,
  Rejected: 8,

  // Training Stage
  Booked: 9,
  InProgress: 10,
  Completed: 11,

  // Final Stage
  CertificateIssued: 12,

  // Closed
  Cancelled: 13,
  Expired: 14,
});

export const EnrollmentStatusMeta = Object.freeze({
  [EnrollmentStatus.None]: { label: "None", className: "bg-slate-100 text-slate-700" },
  [EnrollmentStatus.Draft]: { label: "Draft", className: "bg-amber-50 text-amber-700 border-amber-200" },
  [EnrollmentStatus.Submitted]: { label: "Submitted", className: "bg-sky-50 text-sky-700 border-sky-200" },
  [EnrollmentStatus.Returned]: { label: "Returned", className: "bg-violet-50 text-violet-700 border-violet-200" },
  [EnrollmentStatus.DocumentsPending]: { label: "Documents Pending", className: "bg-orange-50 text-orange-700 border-orange-200" },
  [EnrollmentStatus.PaymentPending]: { label: "Payment Pending", className: "bg-yellow-50 text-yellow-700 border-yellow-200" },
  [EnrollmentStatus.PaymentReceived]: { label: "Payment Received", className: "bg-cyan-50 text-cyan-700 border-cyan-200" },
  [EnrollmentStatus.Approved]: { label: "Approved", className: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  [EnrollmentStatus.Rejected]: { label: "Rejected", className: "bg-rose-50 text-rose-700 border-rose-200" },
  [EnrollmentStatus.Booked]: { label: "Booked", className: "bg-indigo-50 text-indigo-700 border-indigo-200" },
  [EnrollmentStatus.InProgress]: { label: "In Progress", className: "bg-blue-50 text-blue-700 border-blue-200" },
  [EnrollmentStatus.Completed]: { label: "Completed", className: "bg-teal-50 text-teal-700 border-teal-200" },
  [EnrollmentStatus.CertificateIssued]: { label: "Certificate Issued", className: "bg-green-50 text-green-700 border-green-200" },
  [EnrollmentStatus.Cancelled]: { label: "Cancelled", className: "bg-slate-100 text-slate-700 border-slate-200" },
  [EnrollmentStatus.Expired]: { label: "Expired", className: "bg-stone-100 text-stone-700 border-stone-200" },
});

export const EnrollmentStatusTimeline = Object.freeze([
  {
    label: "Payment",
    statuses: [EnrollmentStatus.PaymentPending, EnrollmentStatus.PaymentReceived],
  },
  {
    label: "Application",
    statuses: [EnrollmentStatus.Draft, EnrollmentStatus.Submitted, EnrollmentStatus.Returned],
  },
  {
    label: "Documents",
    statuses: [EnrollmentStatus.DocumentsPending],
  },
  
  {
    label: "Approval",
    statuses: [EnrollmentStatus.Approved, EnrollmentStatus.Rejected],
  },
  {
    label: "Training",
    statuses: [EnrollmentStatus.Booked, EnrollmentStatus.InProgress, EnrollmentStatus.Completed],
  },
  {
    label: "Certificate",
    statuses: [EnrollmentStatus.CertificateIssued],
  },
]);

export const EnrollmentAction = Object.freeze({
  RequestDocuments: "Request Documents",
  RequestPayment: "Request Payment",
  ConfirmPayment: "Confirm Payment",
  Approve: "Approve",
  Reject: "Reject",
  Return: "Return",
  Book: "Book",
  StartTraining: "Start Training",
  Complete: "Complete",
  IssueCertificate: "Issue Certificate",
  Cancel: "Cancel",
  Expire: "Expire",
});

export const EnrollmentActionStatusMap = Object.freeze({
  [EnrollmentAction.RequestDocuments]: EnrollmentStatus.DocumentsPending,
  [EnrollmentAction.RequestPayment]: EnrollmentStatus.PaymentPending,
  [EnrollmentAction.ConfirmPayment]: EnrollmentStatus.PaymentReceived,
  [EnrollmentAction.Approve]: EnrollmentStatus.Approved,
  [EnrollmentAction.Reject]: EnrollmentStatus.Rejected,
  [EnrollmentAction.Return]: EnrollmentStatus.Returned,
  [EnrollmentAction.Book]: EnrollmentStatus.Booked,
  [EnrollmentAction.StartTraining]: EnrollmentStatus.InProgress,
  [EnrollmentAction.Complete]: EnrollmentStatus.Completed,
  [EnrollmentAction.IssueCertificate]: EnrollmentStatus.CertificateIssued,
  [EnrollmentAction.Cancel]: EnrollmentStatus.Cancelled,
  [EnrollmentAction.Expire]: EnrollmentStatus.Expired,
});

export const canEditEnrollment = (status) => [
  EnrollmentStatus.Draft,
  EnrollmentStatus.Returned,
  EnrollmentStatus.DocumentsPending,
].includes(Number(status));

export const canUploadDocuments = (status) =>
  Number(status) === EnrollmentStatus.DocumentsPending;

export const canMakePayment = (status) =>
  Number(status) === EnrollmentStatus.PaymentPending;

export const canViewSummary = (status) => !canEditEnrollment(status);

export const canDownloadEnrollmentDocument = (status) => [
  EnrollmentStatus.Approved,
  EnrollmentStatus.Booked,
  EnrollmentStatus.InProgress,
  EnrollmentStatus.Completed,
  EnrollmentStatus.CertificateIssued,
].includes(Number(status));

export const getStatusMeta = (status) =>
  EnrollmentStatusMeta[Number(status)] || EnrollmentStatusMeta[EnrollmentStatus.None];

export const getEnrollmentStatusName = (status) => getStatusMeta(status).label;

export const getEnrollmentStatusOptions = ({ includeNone = false } = {}) =>
  Object.entries(EnrollmentStatusMeta)
    .filter(([value]) => includeNone || Number(value) !== EnrollmentStatus.None)
    .map(([value, meta]) => ({
      value: Number(value),
      label: meta.label,
    }));
